import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { useLang } from "../../context/LanguageContext";
import { Ed, Reveal } from "../Editorial";
import { fmtTime } from "../../lib/time";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;

// Horario de la semana en la portada: el cliente ve todas las clases de un
// vistazo sin tener que entrar a reservar. Se arma con las clases reales de
// los próximos 7 días, así nunca se desincroniza del horario de verdad.

export const ScheduleSection = () => {
  const { lang, t } = useLang();
  const s = t.schedule;
  const [clases, setClases] = useState([]);

  useEffect(() => {
    axios.get(`${API}/classes?days=7`).then((r) => setClases(r.data || [])).catch(() => {});
  }, []);

  const dias = useMemo(() => {
    const porFecha = new Map();
    for (const c of clases) {
      if (!porFecha.has(c.date)) porFecha.set(c.date, []);
      porFecha.get(c.date).push(c);
    }
    return [...porFecha.entries()]
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([fecha, lista]) => {
        const d = new Date(`${fecha}T12:00:00`);
        return {
          fecha,
          dia: d.toLocaleDateString(lang === "en" ? "en-US" : "es-ES", { weekday: "long" }),
          num: d.getDate(),
          clases: lista.sort((a, b) => a.time.localeCompare(b.time)),
        };
      });
  }, [clases, lang]);

  if (!dias.length) return null;

  return (
    <section id="schedule" className="px-6 md:px-16 py-24 md:py-40 border-t border-white/10" data-testid="schedule-section">
      <Reveal>
        <p className="overline-gold font-sans">
          <Ed text={s.overline} goldItalic={false} />
        </p>
        <h2 className="mt-8 font-serif text-4xl md:text-6xl text-white">
          <Ed text={s.heading} />
        </h2>
        <p className="mt-6 max-w-xl text-sm text-zinc-500 font-light leading-relaxed">{s.intro}</p>
      </Reveal>

      <Reveal className="mt-14">
        <div className="grid gap-px bg-[#1C1C1C] border border-[#1C1C1C] md:grid-cols-4 lg:grid-cols-7">
          {dias.map((d) => (
            <div key={d.fecha} className="bg-[#0C0C0C] p-5 min-h-[190px]" data-testid={`schedule-${d.fecha}`}>
              <div className="flex items-baseline gap-2 pb-3 border-b border-white/10">
                <span className="font-serif text-xl text-white capitalize">{d.dia}</span>
                <span className="text-[0.6rem] uppercase tracking-[0.2em] text-zinc-600">{d.num}</span>
              </div>

              <div className="mt-4 space-y-4">
                {d.clases.map((c) => (
                  <Link
                    key={c.id}
                    to="/book"
                    className="group block"
                    data-testid={`schedule-class-${c.id}`}
                  >
                    <p className="font-serif text-base text-white group-hover:text-[#C5A059] transition-colors duration-300">
                      {fmtTime(c.time)}
                    </p>
                    <p
                      className={`mt-0.5 text-[0.7rem] leading-snug ${
                        c.kind === "floor" ? "text-[#C5A059]" : "text-zinc-400"
                      }`}
                    >
                      {c.kind === "floor" ? s.floorShort : s.rideShort}
                    </p>
                    <p className="text-[0.6rem] text-zinc-600">{c.instructor.split(" ")[0]}</p>
                  </Link>
                ))}
              </div>
            </div>
          ))}
        </div>
      </Reveal>

      <Reveal className="mt-10 flex flex-wrap items-center gap-x-8 gap-y-3">
        <span className="flex items-center gap-2 text-[0.65rem] uppercase tracking-[0.2em] text-zinc-500">
          <span className="h-[2px] w-4 bg-zinc-400" /> {s.rideShort}
        </span>
        <span className="flex items-center gap-2 text-[0.65rem] uppercase tracking-[0.2em] text-zinc-500">
          <span className="h-[2px] w-4 bg-[#C5A059]" /> {s.floorShort}
        </span>
        <Link to="/book" className="ml-auto btn-gold !px-7 !py-3">
          {s.cta}
        </Link>
      </Reveal>
    </section>
  );
};
